import { queryOptions, useQuery } from "@tanstack/react-query";

import { ChainSchema } from "@nitro-ape/shared/models/chain";
import { Protocol } from "@nitro-ape/shared/models/enums/chain";
import {
  AppConfig,
  AppConfigSchema,
} from "@nitro-ape/shared/models/enums/config";
import { DexSchema } from "@nitro-ape/shared/models/pool";
import api from "../../../platform/api/api";
import { QueryConfig } from "../../../lib/query-config";

export const getConfig = async (): Promise<AppConfig | null> => {
  const { data } = await api.get<AppConfig>("/config");

  const result: Partial<AppConfig> = {};

  if (Array.isArray(data.chains)) {
    result.chains = data.chains
      .map((item: unknown) => ChainSchema.safeParse(item))
      .filter((res) => {
        return res.success;
      })
      .map((res) => res.data);
  }

  if (Array.isArray(data.dexes)) {
    result.dexes = data.dexes
      .map((item: unknown) => DexSchema.safeParse(item))
      .filter((res) => res.success)
      .filter(
        (res) => !res.data.protocols.includes(Protocol.PROTOCOL_UNSPECIFIED),
      )
      .map((res) => res.data);
  }

  const levelsResult = AppConfigSchema.shape.levels.safeParse(data.levels);
  if (levelsResult.success) {
    result.levels = levelsResult.data;
  }

  if (Object.keys(result).length > 0) {
    const config: AppConfig = {
      chains: result.chains ?? [],
      dexes: result.dexes ?? [],
      levels: result.levels ?? {},
    };

    return config;
  } else {
    return null;
  }
};

export const getConfigQueryOptions = () => {
  return queryOptions({
    queryKey: ["config"],
    queryFn: () => getConfig(),
    staleTime: Infinity,
  });
};

type UseConfigOptions = {
  queryConfig?: QueryConfig<typeof getConfig>;
};

export const useConfig = ({ queryConfig }: UseConfigOptions = {}) => {
  return useQuery({
    ...getConfigQueryOptions(),
    ...queryConfig,
  });
};

export const useChains = () => {
  const { data, isLoading } = useConfig();
  return {
    chains: data?.chains ?? [],
    isLoading,
  };
};
